import { createRequest } from "helpers/tools";

export const getFailedLeads = async (id, page = 1, pageSize = 10) => {
    return await createRequest({
        suffix: `lead/connection/${id}/failed-leads?page=${page}&page_size=${pageSize}`,
        handleError: true,
        method: "get",
        payload: null,
    });
};

export const retryFailedLeads = async (id, payload) => {
    return await createRequest({
        suffix: `lead/connection/${id}/failed-leads/retry`,
        handleError: true,
        method: "post",
        payload,
    });
};

export const getDuplicateLeads = async (id, page = 1, pageSize = 10) => {
    return await createRequest({
        suffix: `lead/connection/${id}/failed-leads/duplicated?page=${page}&page_size=${pageSize}`,
        handleError: true,
        method: "get",
        payload: null,
    });
};

export const LEAD_HISTORY_MAX_PAGE_SIZE = 100;
export const LEAD_HISTORY_MAX_RANGE_DAYS = 90;

export type LeadHistoryRetrySource = "history" | "debug";

export interface LeadHistoryRow {
    stat_id: number;
    lead_id: string | number;
    date: string;
    status: string;
    last_response?: unknown;
    scheduled: boolean;
    retry_available: boolean;
}

export interface LeadHistoryPagination {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
}

export interface LeadHistoryList {
    leads: LeadHistoryRow[];
    pagination: LeadHistoryPagination;
}

export interface GetLeadHistoryParams {
    page?: number;
    page_size?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
}

/**
 * The list endpoint mirrors failed-leads (`leads` + `pagination`). A Laravel
 * paginator (`data` + page fields) is accepted as well.
 */
export const parseLeadHistoryData = (
    data: unknown,
    pageSize = 10,
): LeadHistoryList => {
    const payload =
        data && typeof data === "object"
            ? (data as Record<string, unknown>)
            : {};
    const rawLeads = payload.leads ?? payload.history ?? payload.data;
    const leads = Array.isArray(rawLeads)
        ? (rawLeads as LeadHistoryRow[])
        : [];
    const rawPagination = payload.pagination;
    const paginationSource =
        rawPagination && typeof rawPagination === "object"
            ? (rawPagination as Record<string, unknown>)
            : payload;

    return {
        leads,
        pagination: {
            current_page: Number(paginationSource.current_page ?? 1),
            per_page: Number(
                paginationSource.per_page ??
                    paginationSource.page_size ??
                    pageSize,
            ),
            total: Number(paginationSource.total ?? leads.length),
            last_page: Number(paginationSource.last_page ?? 1),
        },
    };
};

export const getLeadHistory = async (
    id: string,
    params: GetLeadHistoryParams = {},
    handleError = true,
) => {
    const pageSize = Math.min(
        params.page_size ?? 10,
        LEAD_HISTORY_MAX_PAGE_SIZE,
    );
    const query: Record<string, string | number> = {
        page: params.page ?? 1,
        page_size: pageSize,
    };

    if (params.status) query.status = params.status;
    if (params.start_date && params.end_date) {
        query.start_date = params.start_date;
        query.end_date = params.end_date;
    }

    const response = await createRequest({
        suffix: `lead/connection/${id}/lead-history`,
        handleError,
        method: "get",
        payload: null,
        params: query,
        config: undefined,
    });

    if (!response.status) {
        return {
            ...response,
            data: parseLeadHistoryData(null, pageSize),
        };
    }

    return {
        ...response,
        data: parseLeadHistoryData(response.data, pageSize),
    };
};

export const retryLeadHistory = async (
    id: string,
    payload: { stat_ids: number[]; source: LeadHistoryRetrySource },
    handleError = true,
) => {
    return await createRequest({
        suffix: `lead/connection/${id}/lead-history/retry`,
        handleError,
        method: "post",
        payload,
        params: undefined,
        config: undefined,
    });
};
